/**
 * Offline outbox logic (docs/08 §7), independent of IndexedDB and of the network so it can be
 * tested in Node: `lib/outbox.ts` plugs in the Dexie store, `apiFetch` and the browser events.
 *
 * Rules:
 * - every entry is written to the store before the first attempt (it survives a reload or a crash);
 * - the entry id is also its `Idempotency-Key`: a replay of a request the server already processed
 *   gets the stored response, never a duplicate;
 * - replay in creation order, one at a time; a transient failure (network, 5xx, 408, 429, request
 *   still in progress) stops the round so the order is kept; 401 / password change required stop it
 *   too (the entries wait for the next login of the same user);
 * - the user is checked again before each send: if it changed (logout, login of someone else) the
 *   round stops and the entries stay `pending` for the next login of their author; `abort()` also
 *   cancels the request in flight;
 * - any other refusal (400, 403, 404, 409, 413, 415, 422) puts the entry in `error` with the server
 *   message and the round goes on with the next one: the user decides "riprova" or "scarta";
 * - `enqueue` on an immediate refusal removes the entry and throws, so the form shows the error.
 */
import type { InvalidateEntity, IsoDateTime } from 'core';
import { ApiError, readFileBytes } from './api';

export type OutboxMethod = 'POST' | 'PUT' | 'DELETE';
export type OutboxEntryStatus = 'pending' | 'sending' | 'error';

export interface OutboxFile {
  field: string;
  filename: string;
  /** MIME type declared by the browser. */
  type: string;
  /**
   * Bytes read in the page when the entry was queued. Not the File itself: Safari/iOS 26.5+
   * (WebKit bug 319985) sends a disk-backed File, or one read back from IndexedDB, as an empty
   * body when the page is controlled by a service worker, and the server answers «Nessun file
   * ricevuto». An ArrayBuffer survives IndexedDB everywhere and is sent as an in-memory Blob.
   */
  bytes: ArrayBuffer;
}

/** Entry queued by versions up to 1.0.5: the File itself was stored. Still sent (best effort). */
interface LegacyOutboxFile {
  field: string;
  filename: string;
  blob: Blob;
}

/** Multipart body (photos, scans) kept as bytes and rebuilt into a FormData at each attempt. */
export interface OutboxMultipart {
  fields: Record<string, string>;
  files: OutboxFile[];
}

export interface OutboxError {
  status: number;
  code: string;
  message: string;
}

export interface OutboxEntry {
  /** Auto-increment key of the store: replay order. */
  seq?: number;
  /** UUID, sent as `Idempotency-Key` at every attempt. */
  id: string;
  /** Author: entries are replayed only within a session of the same user. */
  userId: number;
  method: OutboxMethod;
  url: string;
  body?: unknown;
  blobs?: OutboxMultipart;
  /** Description for /sync, e.g. "Report Santa Croce 28/10 10:30". */
  label: string;
  /** Query keys to refresh once the server accepts the entry. */
  invalidates: InvalidateEntity[];
  createdAt: IsoDateTime;
  attempts: number;
  lastAttemptAt: IsoDateTime | null;
  lastError: OutboxError | null;
  status: OutboxEntryStatus;
}

export interface OutboxStore {
  /** Adds the entry and returns its `seq`. */
  add(entry: OutboxEntry): Promise<number>;
  update(seq: number, changes: Partial<OutboxEntry>): Promise<void>;
  remove(seq: number): Promise<void>;
  get(seq: number): Promise<OutboxEntry | undefined>;
  /** Entries ordered by `seq`, optionally of one user. */
  list(userId?: number): Promise<OutboxEntry[]>;
}

export interface OutboxDeps {
  store: OutboxStore;
  /**
   * Performs the HTTP request of an entry (with `entry.id` as Idempotency-Key); throws `ApiError`.
   * `signal` is aborted when the user changes during the round (`abort()`).
   */
  send(entry: OutboxEntry, signal?: AbortSignal): Promise<unknown>;
  isOnline(): boolean;
  /** Logged-in user, null when nobody is logged in (nothing is sent). */
  currentUserId(): number | null;
  /** Called after the server accepted an entry (cache invalidation). */
  onSent?(entry: OutboxEntry, data: unknown): void;
  /** Serialises rounds across tabs (Web Locks); runs `fn` directly when absent. */
  withLock?<T>(fn: () => Promise<T>): Promise<T>;
  newId(): string;
  now?(): Date;
}

export interface EnqueueInput {
  method: OutboxMethod;
  url: string;
  body?: unknown;
  blobs?: OutboxMultipart;
  label: string;
  invalidates?: InvalidateEntity[];
  /** Reuse a key (e.g. a form submitted twice); generated otherwise. */
  id?: string;
}

export type EnqueueResult<T> =
  { status: 'sent'; data: T } | { status: 'queued'; entry: OutboxEntry };

export interface FlushSummary {
  sent: number;
  failed: number;
  /** Why the round stopped before the end, if it did. */
  stoppedBy: 'offline' | 'transient' | 'unauthorized' | 'no-user' | null;
}

type Outcome = { ok: true; data: unknown } | { ok: false; error: ApiError };

/** Failures worth retrying later without user action. */
export function isTransientError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return (
    err.isNetworkError ||
    err.status >= 500 ||
    err.status === 408 ||
    err.status === 429 ||
    err.code === 'IDEMPOTENCY_IN_PROGRESS'
  );
}

/** Failures that need a new login (or the password change) before anything else can be sent. */
export function isAuthError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.code === 'PASSWORD_CHANGE_REQUIRED');
}

function toOutboxError(err: unknown): OutboxError {
  if (err instanceof ApiError) return { status: err.status, code: err.code, message: err.message };
  return {
    status: 0,
    code: 'CLIENT_ERROR',
    message: err instanceof Error ? err.message : 'Errore imprevisto',
  };
}

function asApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  const e = toOutboxError(err);
  return new ApiError(e.status, e.code, e.message);
}

/**
 * Splits a FormData into storable fields and file bytes, read now, in the page. A file that reads
 * as empty is refused at once (`EMPTY_FILE`): queuing it would only fail later on the server.
 */
export async function multipartFromFormData(formData: FormData): Promise<OutboxMultipart> {
  const result: OutboxMultipart = { fields: {}, files: [] };
  const parts: [string, FormDataEntryValue][] = [];
  formData.forEach((value, field) => parts.push([field, value]));
  for (const [field, value] of parts) {
    if (typeof value === 'string') {
      result.fields[field] = value;
      continue;
    }
    // a read the browser refuses is a 400 FILE_UNREADABLE (readFileBytes); an empty file is refused too
    const bytes = await readFileBytes(value);
    if (bytes.byteLength === 0) {
      throw new ApiError(
        400,
        'EMPTY_FILE',
        'Il file è vuoto o non leggibile: rifai la foto o scegli di nuovo il file',
      );
    }
    result.files.push({ field, filename: value.name || 'file', type: value.type, bytes });
  }
  return result;
}

/** Rebuilds the FormData with in-memory Blobs (never the File object stored by old versions). */
export function formDataFromMultipart(multipart: OutboxMultipart): FormData {
  const formData = new FormData();
  for (const [field, value] of Object.entries(multipart.fields)) formData.append(field, value);
  for (const file of multipart.files as (OutboxFile | LegacyOutboxFile)[]) {
    const blob = 'bytes' in file && file.bytes ? new Blob([file.bytes], { type: file.type }) : null;
    const legacy = 'blob' in file && file.blob instanceof Blob ? file.blob : null;
    const value = blob ?? legacy;
    if (!value) {
      throw new ApiError(
        400,
        'EMPTY_FILE',
        'Allegato in coda non più leggibile: scarta la voce e rifai la foto',
      );
    }
    formData.append(file.field, value, file.filename);
  }
  return formData;
}

export function createOutbox(deps: OutboxDeps) {
  const now = () => (deps.now?.() ?? new Date()).toISOString();
  const withLock = deps.withLock ?? (<T>(fn: () => Promise<T>) => fn());
  /** Outcome of the entries processed by the last rounds, read once by `enqueue`. */
  const outcomes = new Map<number, Outcome>();
  let running: Promise<FlushSummary> | null = null;
  /** Aborts the requests of the round in progress (change of user). */
  let controller: AbortController | null = null;
  const listeners = new Set<(flushing: boolean) => void>();

  const setRunning = (p: Promise<FlushSummary> | null) => {
    running = p;
    for (const l of listeners) l(p != null);
  };

  async function round(): Promise<FlushSummary> {
    const ctrl = new AbortController();
    controller = ctrl;
    try {
      return await roundOf(ctrl.signal);
    } finally {
      if (controller === ctrl) controller = null;
    }
  }

  async function roundOf(signal: AbortSignal): Promise<FlushSummary> {
    const summary: FlushSummary = { sent: 0, failed: 0, stoppedBy: null };
    const userId = deps.currentUserId();
    if (userId == null) return { ...summary, stoppedBy: 'no-user' };
    // the session cookie belongs to `userId` only while it is still the logged-in user
    const userChanged = () => signal.aborted || deps.currentUserId() !== userId;
    let cursor = 0;
    for (;;) {
      if (userChanged()) return { ...summary, stoppedBy: 'no-user' };
      if (!deps.isOnline()) return { ...summary, stoppedBy: 'offline' };
      // re-read at each step: entries added during the round are sent in the same round
      const entries = await deps.store.list(userId);
      const next = entries.find(
        (e) => (e.seq ?? 0) > cursor && (e.status === 'pending' || e.status === 'sending'),
      );
      if (!next || next.seq == null) return summary;
      // checked again right before the request (the store read is asynchronous)
      if (userChanged()) return { ...summary, stoppedBy: 'no-user' };
      const seq = next.seq;
      cursor = seq;
      const attempt: Partial<OutboxEntry> = {
        status: 'sending',
        attempts: next.attempts + 1,
        lastAttemptAt: now(),
      };
      await deps.store.update(seq, attempt);
      try {
        const data = await deps.send({ ...next, ...attempt }, signal);
        await deps.store.remove(seq);
        outcomes.set(seq, { ok: true, data });
        summary.sent++;
        deps.onSent?.(next, data);
      } catch (err) {
        if (userChanged()) {
          // aborted, or answered for another session (e.g. 403 with the new user's cookie): the
          // entry waits for the next login of its author; the attempt does not count
          await deps.store.update(seq, { status: 'pending', attempts: next.attempts });
          return { ...summary, stoppedBy: 'no-user' };
        }
        const lastError = toOutboxError(err);
        if (isTransientError(err)) {
          await deps.store.update(seq, { status: 'pending', lastError });
          return { ...summary, stoppedBy: 'transient' };
        }
        if (isAuthError(err)) {
          await deps.store.update(seq, { status: 'pending', lastError });
          return { ...summary, stoppedBy: 'unauthorized' };
        }
        await deps.store.update(seq, { status: 'error', lastError });
        outcomes.set(seq, { ok: false, error: asApiError(err) });
        summary.failed++;
      }
    }
  }

  /** Sends the pending entries of the current user, in order. Concurrent calls share the round. */
  function flush(): Promise<FlushSummary> {
    if (running) return running;
    if (!deps.isOnline()) return Promise.resolve({ sent: 0, failed: 0, stoppedBy: 'offline' });
    const p = withLock(round).finally(() => setRunning(null));
    setRunning(p);
    return p;
  }

  /**
   * Sends a mutation through the queue. Online: it is sent now (after any older pending entry) and
   * the server answer is returned; an immediate refusal throws the `ApiError` and leaves nothing in
   * the queue. Offline or on a transient failure: it stays queued and `{status:'queued'}` returns.
   */
  async function enqueue<T>(input: EnqueueInput): Promise<EnqueueResult<T>> {
    const userId = deps.currentUserId();
    if (userId == null) {
      throw new ApiError(401, 'UNAUTHORIZED', 'Sessione scaduta: accedi di nuovo');
    }
    const entry: OutboxEntry = {
      id: input.id ?? deps.newId(),
      userId,
      method: input.method,
      url: input.url,
      body: input.body,
      blobs: input.blobs,
      label: input.label,
      invalidates: input.invalidates ?? [],
      createdAt: now(),
      attempts: 0,
      lastAttemptAt: null,
      lastError: null,
      status: 'pending',
    };
    const seq = await deps.store.add(entry);
    const saved: OutboxEntry = { ...entry, seq };
    if (!deps.isOnline()) return { status: 'queued', entry: saved };

    // a round already running may stop before reaching the new entry: run another one after it
    if (running) await running;
    if (!outcomes.has(seq)) await flush();
    const outcome = outcomes.get(seq);
    outcomes.delete(seq);
    if (!outcome) return { status: 'queued', entry: (await deps.store.get(seq)) ?? saved };
    if (outcome.ok) return { status: 'sent', data: outcome.data as T };
    await deps.store.remove(seq);
    throw outcome.error;
  }

  /** Puts an entry in error back in the queue and starts a round. */
  async function retry(seq: number): Promise<FlushSummary> {
    await deps.store.update(seq, { status: 'pending', lastError: null });
    return flush();
  }

  /** Drops an entry (the user decided not to send it). */
  async function discard(seq: number): Promise<void> {
    outcomes.delete(seq);
    await deps.store.remove(seq);
  }

  /**
   * Cancels the round in progress (the request in flight is aborted, its entry stays `pending`)
   * and resolves when the round has ended. Called when the user changes, before the session
   * cookie does.
   */
  async function abort(): Promise<void> {
    controller?.abort();
    if (running) await running.catch(() => undefined);
  }

  return {
    enqueue,
    flush,
    retry,
    discard,
    abort,
    isFlushing: () => running != null,
    /** Notified when a round starts or ends. */
    subscribe(listener: (flushing: boolean) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export type Outbox = ReturnType<typeof createOutbox>;

/** In-memory store (tests, and fallback when IndexedDB is not available). */
export function createMemoryStore(): OutboxStore {
  const rows = new Map<number, OutboxEntry>();
  let nextSeq = 1;
  return {
    async add(entry) {
      const seq = nextSeq++;
      rows.set(seq, { ...entry, seq });
      return seq;
    },
    async update(seq, changes) {
      const row = rows.get(seq);
      if (row) rows.set(seq, { ...row, ...changes });
    },
    async remove(seq) {
      rows.delete(seq);
    },
    async get(seq) {
      const row = rows.get(seq);
      return row ? { ...row } : undefined;
    },
    async list(userId) {
      return [...rows.values()]
        .filter((r) => userId == null || r.userId === userId)
        .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
        .map((r) => ({ ...r }));
    },
  };
}
